<template>
  <div class="relative" ref="filterContainer">
    <!-- Trigger Button with Selected Pills -->
    <div class="flex flex-wrap items-center gap-1">
      <!-- AND/OR Toggle (shown when 2+ include tags selected and advanced mode enabled) -->
      <button
        v-if="enableAdvancedFilters && includedTags.length >= 2"
        @click.stop="toggleFilterMode"
        type="button"
        class="inline-flex items-center px-1.5 py-0.5 text-[10px] font-bold rounded border transition-colors"
        :class="filterMode === 'and'
          ? 'bg-amber-100 text-amber-700 border-amber-300'
          : 'bg-gray-100 text-gray-500 border-gray-300'"
        :title="filterMode === 'and' ? 'AND: must match all tags' : 'OR: match any tag'"
      >
        {{ filterMode.toUpperCase() }}
      </button>

      <!-- Selected Tag Pills (include + exclude) -->
      <Tag
        v-for="tag in selectedTags"
        :key="tag.id"
        :label="tag.name"
        :color="isExcluded(tag.id) ? 'red' : color"
        :removable="true"
        :clickable="enableAdvancedFilters"
        :strikethrough="isExcluded(tag.id)"
        :title="enableAdvancedFilters ? (isExcluded(tag.id) ? 'Click to include' : 'Click to exclude (NOT)') : ''"
        @click.stop="enableAdvancedFilters && toggleExclude(tag.id)"
        @remove="removeTag(tag.id)"
      >
        <template v-if="isExcluded(tag.id)" #prefix>
          <span class="font-bold text-red-500 mr-0.5">NOT</span>
        </template>
      </Tag>

      <!-- Add Filter Button -->
      <button
        @click.stop="togglePanel"
        type="button"
        class="inline-flex items-center gap-1 px-2.5 py-1 text-sm rounded-full border border-dashed transition-colors"
        :class="addButtonClasses"
      >
        <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4"></path>
        </svg>
        {{ buttonLabel }}
      </button>
    </div>

    <!-- Popover Panel -->
    <Teleport to="body">
      <div
        v-show="isOpen"
        ref="popoverPanel"
        class="fixed z-[9999] bg-white border border-gray-300 rounded-lg shadow-xl w-72"
        :style="panelStyle"
      >
        <!-- Header -->
        <div class="px-4 py-3 border-b border-gray-200 bg-gray-50 rounded-t-lg">
          <div class="flex items-center justify-between">
            <h3 class="text-sm font-semibold text-gray-900">{{ label }}</h3>
            <button
              @click.stop="closePanel"
              type="button"
              class="text-gray-400 hover:text-gray-600"
            >
              <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
              </svg>
            </button>
          </div>
        </div>

        <!-- Search -->
        <div class="px-4 py-2 border-b border-gray-100">
          <div class="relative">
            <input
              v-model="searchQuery"
              type="text"
              placeholder="Search tags..."
              class="w-full pl-8 pr-3 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-dark"
            />
            <svg class="absolute left-2.5 top-2 w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"></path>
            </svg>
          </div>
        </div>

        <!-- Quick Actions -->
        <div class="px-4 py-2 border-b border-gray-100 flex items-center justify-between">
          <button
            @click.stop="selectAll"
            type="button"
            class="text-xs text-primary-600 hover:text-primary-800 font-medium"
          >
            Select All
          </button>
          <button
            @click.stop="clearAll"
            type="button"
            class="text-xs text-gray-600 hover:text-gray-800 font-medium"
          >
            Clear All
          </button>
        </div>

        <!-- Tag List -->
        <div class="max-h-48 overflow-y-auto">
          <LoadingIndicator v-if="loading" class="px-4 py-6" message="Loading tags..." />

          <div v-else-if="filteredTags.length === 0" class="px-4 py-6 text-center text-sm text-gray-500">
            {{ searchQuery ? 'No matching tags found' : 'No tags available' }}
          </div>

          <!-- Grouped by type -->
          <template v-else-if="isGrouped">
            <template v-for="group in groupedTags" :key="group.typeName">
              <div
                class="px-4 py-1.5 text-xs font-semibold text-gray-500 uppercase tracking-wider bg-gray-50 cursor-pointer select-none flex items-center justify-between"
                @click.stop="toggleGroup(group.typeName)"
              >
                <span>{{ group.typeName }}</span>
                <span class="text-[10px] text-gray-400">
                  {{ isGroupExpanded(group.typeName) ? '▾' : '▸' }} {{ group.tags.length }}
                </span>
              </div>
              <template v-if="isGroupExpanded(group.typeName)">
                <label
                  v-for="tag in group.tags"
                  :key="tag.id"
                  class="flex items-center px-4 py-2 hover:bg-gray-50 cursor-pointer transition-colors"
                  @click.stop
                >
                  <input
                    type="checkbox"
                    :value="tag.id"
                    :checked="isSelected(tag.id)"
                    @change="toggleTag(tag.id)"
                    @click.stop
                    class="h-4 w-4 shrink-0 rounded cursor-pointer focus:ring-blue-dark border-gray-300"
                    :class="checkboxClasses"
                  />
                  <span class="ml-3 text-sm text-gray-700 truncate" :title="tag.name">{{ tag.name }}</span>
                  <span v-if="tag.count !== undefined" class="ml-auto text-xs text-gray-400">
                    {{ tag.count }}
                  </span>
                </label>
              </template>
            </template>
          </template>

          <!-- Flat list -->
          <label
            v-else
            v-for="tag in filteredTags"
            :key="tag.id"
            class="flex items-center px-4 py-2 hover:bg-gray-50 cursor-pointer transition-colors"
            @click.stop
          >
            <input
              type="checkbox"
              :value="tag.id"
              :checked="isSelected(tag.id)"
              @change="toggleTag(tag.id)"
              @click.stop
              class="h-4 w-4 shrink-0 rounded cursor-pointer focus:ring-blue-dark border-gray-300"
              :class="checkboxClasses"
            />
            <span class="ml-3 text-sm text-gray-700">{{ tag.name }}</span>
            <span v-if="tag.count !== undefined" class="ml-auto text-xs text-gray-400">
              {{ tag.count }}
            </span>
          </label>
        </div>

        <!-- Footer -->
        <div class="px-4 py-2 border-t border-gray-200 bg-gray-50 rounded-b-lg">
          <p class="text-xs text-gray-500">
            {{ modelValue.length + excludedTags.length }} of {{ availableTags.length }} selected
          </p>
        </div>
      </div>
    </Teleport>
  </div>
</template>

<script>
import axios from 'axios'
import LoadingIndicator from '@/components/widgetcomponents/library/LoadingIndicator.vue'
import Tag from '@/components/widgetcomponents/library/Tag.vue'

export default {
  name: 'InfoTagFilter',
  components: { LoadingIndicator, Tag },
  props: {
    modelValue: {
      type: Array,
      default: () => []
      // Tag IDs to filter IN (positive). Disjoint from excludedTags.
    },
    excludedTags: {
      type: Array,
      default: () => []
      // Tag IDs to filter OUT (NOT). Disjoint from modelValue.
    },
    filterMode: {
      type: String,
      default: 'or'
      // 'or' = match any tag, 'and' = match all tags
    },
    infoTagType: {
      type: String,
      default: null
      // e.g., 'Implementation Status', 'Region', etc.
      // When null, fetches all tags and groups by type
    },
    label: {
      type: String,
      default: 'Filter by Tag'
    },
    buttonLabel: {
      type: String,
      default: 'Add Filter'
    },
    color: {
      type: String,
      default: 'emerald'
      // Options: 'emerald', 'indigo', 'purple', 'blue', 'amber', 'rose'
    },
    enableAdvancedFilters: {
      type: Boolean,
      default: false
      // When true, shows AND/OR toggle and allows click-to-exclude (NOT) on pills
    }
  },
  emits: ['update:modelValue', 'update:excludedTags', 'update:filterMode', 'change'],
  data() {
    return {
      isOpen: false,
      loading: false,
      searchQuery: '',
      availableTags: [],
      expandedGroups: {},
      panelStyle: {
        top: '0px',
        left: '0px'
      }
    }
  },
  computed: {
    selectedTags() {
      // Pills shown in the trigger row: union of include + exclude.
      const displayedIds = new Set([...this.modelValue, ...this.excludedTags])
      return this.availableTags.filter(tag => displayedIds.has(tag.id))
    },
    includedTags() {
      return this.availableTags.filter(tag => this.modelValue.includes(tag.id))
    },
    isGrouped() {
      return !this.infoTagType
    },
    filteredTags() {
      if (!this.searchQuery.trim()) {
        return this.availableTags
      }
      const query = this.searchQuery.toLowerCase()
      return this.availableTags.filter(tag =>
        tag.name.toLowerCase().includes(query)
      )
    },
    groupedTags() {
      const groups = {}
      for (const tag of this.filteredTags) {
        const typeName = tag.typeName || 'Other'
        if (!groups[typeName]) {
          groups[typeName] = { typeName, tags: [] }
        }
        groups[typeName].tags.push(tag)
      }
      return Object.values(groups).sort((a, b) => a.typeName.localeCompare(b.typeName))
    },
    addButtonClasses() {
      const colorMap = {
        emerald: 'border-emerald-300 text-emerald-600 hover:bg-emerald-50',
        indigo: 'border-primary-300 text-primary-600 hover:bg-primary-50',
        purple: 'border-purple-300 text-purple-600 hover:bg-purple-50',
        blue: 'border-blue-300 text-blue-600 hover:bg-blue-50',
        amber: 'border-amber-300 text-amber-600 hover:bg-amber-50',
        rose: 'border-rose-300 text-rose-600 hover:bg-rose-50'
      }
      return colorMap[this.color] || colorMap.emerald
    },
    checkboxClasses() {
      const colorMap = {
        emerald: 'text-emerald-600',
        indigo: 'text-primary-600',
        purple: 'text-purple-600',
        blue: 'text-blue-600',
        amber: 'text-amber-600',
        rose: 'text-rose-600'
      }
      return colorMap[this.color] || colorMap.emerald
    }
  },
  methods: {
    async fetchTags() {
      this.loading = true
      try {
        const params = {}
        if (this.infoTagType) {
          params.infoTagType_name = this.infoTagType
        }
        const response = await axios.get('/api/infotags/', { params })
        if (response.data) {
          // When grouped, fetch type names for display
          let typeNameMap = {}
          if (!this.infoTagType) {
            try {
              const typesResponse = await axios.get('/api/infotagtypes/')
              if (typesResponse.data) {
                for (const t of typesResponse.data) {
                  typeNameMap[t.id] = t.name
                }
              }
            } catch {
              // Fall back to type ID if types endpoint fails
            }
          }
          this.availableTags = response.data.map(tag => ({
            id: tag.id,
            name: tag.name,
            count: tag.entity_count,
            typeName: typeNameMap[tag.infoTagType] || this.infoTagType || 'Other'
          }))
        }
      } catch (error) {
        console.error('Error fetching info tags:', error)
        this.availableTags = []
      } finally {
        this.loading = false
      }
    },

    updatePanelPosition() {
      if (!this.$refs.filterContainer) return

      const rect = this.$refs.filterContainer.getBoundingClientRect()
      const panelHeight = 400 // approximate max height
      const panelWidth = 288 // w-72

      let top = rect.bottom + 8
      let left = rect.left

      // Position above if not enough space below
      const spaceBelow = window.innerHeight - rect.bottom
      if (spaceBelow < panelHeight && rect.top > spaceBelow) {
        top = rect.top - panelHeight - 8
      }

      // Keep within viewport horizontally
      if (left + panelWidth > window.innerWidth - 16) {
        left = window.innerWidth - panelWidth - 16
      }

      this.panelStyle = {
        position: 'fixed',
        top: `${top}px`,
        left: `${left}px`
      }
    },

    toggleGroup(typeName) {
      this.expandedGroups = { ...this.expandedGroups, [typeName]: !this.expandedGroups[typeName] }
    },

    isGroupExpanded(typeName) {
      if (this.searchQuery.trim()) return true
      if (typeName in this.expandedGroups) return this.expandedGroups[typeName]
      return this.groupHasSelectedTags(typeName)
    },

    groupHasSelectedTags(typeName) {
      const group = this.groupedTags.find(g => g.typeName === typeName)
      if (!group) return false
      return group.tags.some(tag =>
        this.modelValue.includes(tag.id) || this.excludedTags.includes(tag.id)
      )
    },

    togglePanel() {
      this.isOpen = !this.isOpen
      if (this.isOpen) {
        this.searchQuery = ''
        this.expandedGroups = {}
        this.$nextTick(() => {
          this.updatePanelPosition()
        })
      }
    },

    closePanel() {
      this.isOpen = false
    },

    isSelected(tagId) {
      // Checkbox is checked if the tag is configured either way (include or NOT).
      return this.modelValue.includes(tagId) || this.excludedTags.includes(tagId)
    },

    toggleTag(tagId) {
      // Panel checkbox: a single click clears whichever side the tag lives on,
      // or adds it as a positive include if it's not configured at all.
      if (this.modelValue.includes(tagId)) {
        const newValue = this.modelValue.filter(id => id !== tagId)
        this.$emit('update:modelValue', newValue)
        this.$emit('change', newValue)
      } else if (this.excludedTags.includes(tagId)) {
        const newExcluded = this.excludedTags.filter(id => id !== tagId)
        this.$emit('update:excludedTags', newExcluded)
        this.$emit('change', this.modelValue)
      } else {
        const newValue = [...this.modelValue, tagId]
        this.$emit('update:modelValue', newValue)
        this.$emit('change', newValue)
      }
    },

    removeTag(tagId) {
      // Pill X-button: drop the tag from whichever side it lives on.
      if (this.excludedTags.includes(tagId)) {
        const newExcluded = this.excludedTags.filter(id => id !== tagId)
        this.$emit('update:excludedTags', newExcluded)
        this.$emit('change', this.modelValue)
      } else {
        const newValue = this.modelValue.filter(id => id !== tagId)
        this.$emit('update:modelValue', newValue)
        this.$emit('change', newValue)
      }
    },

    isExcluded(tagId) {
      return this.excludedTags.includes(tagId)
    },

    toggleExclude(tagId) {
      // Pill click: move the tag between include and exclude.
      if (this.excludedTags.includes(tagId)) {
        this.$emit('update:excludedTags', this.excludedTags.filter(id => id !== tagId))
        this.$emit('update:modelValue', [...this.modelValue, tagId])
      } else if (this.modelValue.includes(tagId)) {
        this.$emit('update:modelValue', this.modelValue.filter(id => id !== tagId))
        this.$emit('update:excludedTags', [...this.excludedTags, tagId])
      }
      this.$emit('change', this.modelValue)
    },

    toggleFilterMode() {
      const newMode = this.filterMode === 'or' ? 'and' : 'or'
      this.$emit('update:filterMode', newMode)
      this.$emit('change', this.modelValue)
    },

    selectAll() {
      // Promote all visible tags to positive includes. Tags currently NOT'd
      // stay NOT'd — Select All shouldn't silently flip an exclude.
      const excludedSet = new Set(this.excludedTags)
      const additions = this.filteredTags
        .map(tag => tag.id)
        .filter(id => !excludedSet.has(id))
      const newValue = [...new Set([...this.modelValue, ...additions])]
      this.$emit('update:modelValue', newValue)
      this.$emit('change', newValue)
    },

    clearAll() {
      this.$emit('update:modelValue', [])
      this.$emit('update:excludedTags', [])
      this.$emit('change', [])
    },

    handleClickOutside(event) {
      const container = this.$refs.filterContainer
      const panel = this.$refs.popoverPanel
      if (container && !container.contains(event.target) &&
          (!panel || !panel.contains(event.target))) {
        this.closePanel()
      }
    }
  },

  watch: {
    infoTagType: {
      immediate: false,
      handler() {
        this.fetchTags()
      }
    }
  },

  mounted() {
    document.addEventListener('click', this.handleClickOutside)
    window.addEventListener('scroll', this.updatePanelPosition, true)
    window.addEventListener('resize', this.updatePanelPosition)
    // Fetch tags on mount
    this.fetchTags()
  },

  beforeUnmount() {
    document.removeEventListener('click', this.handleClickOutside)
    window.removeEventListener('scroll', this.updatePanelPosition, true)
    window.removeEventListener('resize', this.updatePanelPosition)
  }
}
</script>

<style scoped>
/* Custom scrollbar */
.overflow-y-auto::-webkit-scrollbar {
  width: 6px;
}

.overflow-y-auto::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 3px;
}

.overflow-y-auto::-webkit-scrollbar-thumb {
  background: #ccc;
  border-radius: 3px;
}

.overflow-y-auto::-webkit-scrollbar-thumb:hover {
  background: #999;
}
</style>
